import { ref, readonly } from "vue";
import { CcPlatformSdk } from "@cc-consulting-nv/ccsdk";
import type { AuthTokens } from "@cc-consulting-nv/ccsdk";
import { resolveApiBaseUrl } from "@/utils/env";
import { authService } from "@/services/authService";

// SDK singleton instance
let sdkInstance: CcPlatformSdk | null = null;

// Reactive state for SDK initialization
const isInitialized = ref(false);
const initError = ref<string | null>(null);

// Storage keys (matching authService to avoid circular dependency)
const TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";

/**
 * Get the base URL from environment
 * In production, uses window.location.origin so a single build works on any domain
 */
function getBaseUrl(): string {
  return resolveApiBaseUrl();
}

/**
 * Get initial tokens from localStorage or environment
 * Priority: localStorage > environment variable
 */
function getInitialTokens(): AuthTokens | undefined {
  // First check localStorage (survives HMR and page refresh)
  // IMPORTANT: Read directly from localStorage to avoid circular dependency with authService
  const storedToken = localStorage.getItem(TOKEN_KEY);
  const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

  if (storedToken) {
    return {
      accessToken: storedToken,
      refreshToken: storedRefresh || undefined,
    };
  }

  // Fallback to environment variable (for development)
  const envToken = import.meta.env.VITE_API_TOKEN;
  if (envToken) {
    return {
      accessToken: envToken,
    };
  }
  return undefined;
}

/**
 * Clear all auth data and redirect to login
 * Called on 401 (after refresh attempt fails)
 */
async function handleTokenExpiration(): Promise<void> {
  const current = window.location.pathname + window.location.search;

  // Avoid redirect loop: if already on /login, just clear state and return
  if (current === "/login" || current.startsWith("/login?")) {
    console.log("🔒 401 on login page - clearing auth state only");
    await authService.logout();
    try {
      await sdkInstance?.clearCache();
    } catch (error) {
      console.error("Failed to clear SDK cache:", error);
    }
    return;
  }

  console.log("🔒 401 Unauthorized - clearing auth and redirecting to login");

  await authService.logout();

  try {
    await sdkInstance?.clearCache();
    console.log("✅ Cleared SDK cache on token expiration");
  } catch (error) {
    console.error("Failed to clear SDK cache:", error);
  }

  const loginPath = `/login?redirect=${encodeURIComponent(current)}`;
  window.location.href = loginPath;
}

/**
 * Attempt to refresh the access token using the refresh token
 */
async function refreshTokens(): Promise<AuthTokens> {
  const tokens = await authService.refreshTokens();
  console.log("✅ Token refreshed successfully");
  return tokens;
}

/**
 * Initialize the SDK instance
 */
function initializeSdk(): CcPlatformSdk {
  if (sdkInstance) {
    return sdkInstance;
  }

  const baseUrl = getBaseUrl();
  const tokens = getInitialTokens();

  // Check environment variable for SDK logging (VITE_SDK_ENABLE_LOGGING)
  // Explicitly check for "true" or "1", default to false
  // This explicitly disables logging unless the env var is set to "true" or "1"
  const envLogging = import.meta.env.VITE_SDK_ENABLE_LOGGING;
  const enableLogging = Boolean(
    envLogging === "true" || 
    envLogging === "1" || 
    envLogging === true
  );

  // Check environment variable for MessagePack (VITE_SDK_USE_MSGPACK)
  const envMsgpack = import.meta.env.VITE_SDK_USE_MSGPACK;
  const useMsgpack = Boolean(
    envMsgpack === "true" ||
    envMsgpack === "1" ||
    envMsgpack === true
  );

  sdkInstance = new CcPlatformSdk({
    baseUrl,
    tokens,
    enableLogging, // Explicitly pass false if not enabled (overrides any localStorage/window settings)
    useMsgpack, // Enable MessagePack binary format for smaller/faster API responses
    onRefreshTokens: async () => {
      // Attempt to refresh tokens
      return refreshTokens();
    },
    onUnauthorized: async () => {
      // Handle 401 errors after refresh failed - clear all data and redirect to login
      console.warn("Unauthorized after refresh attempt - redirecting to login");
      await handleTokenExpiration();
    },
  });

  isInitialized.value = true;
  return sdkInstance;
}

/**
 * Composable to access the CC Platform SDK
 */
export function useSdk() {
  // Initialize SDK on first access
  if (!sdkInstance) {
    try {
      initializeSdk();
    } catch (error) {
      initError.value =
        error instanceof Error ? error.message : "Failed to initialize SDK";
      console.error("SDK initialization error:", error);
    }
  }

  /**
   * Set authentication tokens
   */
  function setTokens(tokens: AuthTokens | null) {
    if (sdkInstance) {
      sdkInstance.setTokens(tokens);
    }
  }

  /**
   * Get current authentication tokens
   */
  function getTokens(): AuthTokens | null {
    return sdkInstance?.getTokens() ?? null;
  }

  /**
   * Check if user is authenticated (has tokens)
   */
  function hasTokens(): boolean {
    const tokens = getTokens();
    return Boolean(tokens?.accessToken);
  }

  /**
   * Clear all cached data
   */
  async function clearCache() {
    if (sdkInstance) {
      await sdkInstance.clearCache();
    }
  }

  return {
    // SDK instance (may be null if initialization failed)
    sdk: sdkInstance!,

    // State
    isInitialized: readonly(isInitialized),
    initError: readonly(initError),

    // Methods
    setTokens,
    getTokens,
    hasTokens,
    clearCache,
  };
}

// Export for direct SDK access (advanced use cases)
export function getSdk(): CcPlatformSdk {
  if (!sdkInstance) {
    initializeSdk();
  }
  return sdkInstance!;
}

/** API base URL (same as SDK uses) - for broadcasting auth, etc. */
export function getApiBaseUrl(): string {
  return getBaseUrl();
}
